import { FunctionComponent } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';

import { useTranslation } from '../../../../utils/useTranslation';
import { Typography } from '../../components/Typography/Typography';
import { Button } from '../../components/Button/Button';
import ns from '../../translations';
import { useErrorRecovery } from '../../hooks/ErrorRecoveryProvider';
import { useTracker } from '../../hooks/useTracker';
import {
  ReportDialog,
  ReportDialogProps,
} from '../../components/ReportDialog/ReportDialog';
import { ErrorDetails } from '../../hooks/PlayerErrorProvider';
import { useApis } from '../../hooks/ApisProvider';
import { useProvider } from '../../hooks/useProvider';
import { useConfig } from '../../hooks/ConfigProvider';
import { useReportContext } from '../../hooks/useReportContext';
import { usePlayerFullscreen } from '../../hooks/usePlayerFullscreen';

export interface ErrorLayoutProps {
  errorDetails: ErrorDetails;
}

const errorCodeMessages: Record<number, string> = {
  246014: 'Your playback session has expired.',
  224003: "Your browser doesn't support playback of this content.",
  246007: "We couldn't verify your playback rights.",
};
export const ErrorLayout: FunctionComponent<ErrorLayoutProps> = ({
  errorDetails,
}) => {
  const { t } = useTranslation(ns);
  const { handleRetry, handleReload, canRecover } = useErrorRecovery();
  const { trackButtonClick } = useTracker();
  const { trackErrorView } = useTracker();
  const { vgtvApi } = useApis();
  const { configProvider } = useProvider();
  const { getVariant } = useConfig();
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const { getDefaultContext } = useReportContext();
  const { exitFullscreen } = usePlayerFullscreen();

  const canReportIssue =
    getVariant('error-reporting') === 'enabled' && configProvider === 'vgtv';

  const { code, referenceId, message: rawMessage, sourceError } = errorDetails;

  const mappedMessage = code ? errorCodeMessages[code] : undefined;
  const message = mappedMessage
    ? t(mappedMessage, {}, { noJSX: true })
    : rawMessage;

  useEffect(() => {
    trackErrorView(code);
  }, [code, trackErrorView]);

  const handleReportClick = useCallback(() => {
    trackButtonClick('report-error');
    setIsReportDialogOpen(true);
    exitFullscreen();
  }, [trackButtonClick, exitFullscreen]);

  const handleReportSubmit: ReportDialogProps['onSubmit'] = useCallback(
    async (values) => {
      setIsReportDialogOpen(false);

      const { tags, ...context } = await getDefaultContext.current();

      const prefix = code ? `[${code}] ` : '';

      await vgtvApi.createFeedback({
        ...values,
        event: {
          message: prefix + message,
          captureContext: {
            ...context,
            extra: {
              sourceError,
            },
            tags: {
              ...tags,
              'error.code': code,
              'error.referenceId': referenceId,
            },
          },
        },
      });
    },
    [code, referenceId, getDefaultContext, message, sourceError, vgtvApi]
  );

  const title = t('Something went wrong', {}, { noJSX: true });

  return (
    <div className="vgp-error-layout">
      <div className="vgp-error-layout-text-content">
        <div className="vgp-error-layout-title">
          <Typography variant="title" size="medium" color="white" bold>
            {title}
          </Typography>
        </div>
        <div className="vgp-error-layout-description">
          {message ? (
            <Typography variant="label" size="large" color="white">
              <span dangerouslySetInnerHTML={{ __html: message }} />
            </Typography>
          ) : null}

          {code ? (
            <Typography variant="label" size="medium" color="white">
              {t('Error code')}: {code}
            </Typography>
          ) : null}
          {referenceId ? (
            <Typography variant="label" size="medium" color="white">
              {t('Reference ID')}: {referenceId}
            </Typography>
          ) : null}
        </div>
      </div>
      <div className="vgp-buttons vgp-error-layout-buttons">
        {canRecover ? (
          <>
            <Button onClick={handleRetry}>{t('Try again')}</Button>
            {canReportIssue ? (
              <Button onClick={handleReportClick}>{t('Report issue')}</Button>
            ) : null}
          </>
        ) : canReportIssue ? (
          <Button onClick={handleReportClick}>{t('Report issue')}</Button>
        ) : (
          <Button onClick={handleReload}>{t('Reload')}</Button>
        )}
      </div>
      {canReportIssue && (
        <ReportDialog
          isOpen={isReportDialogOpen}
          onClose={() => setIsReportDialogOpen(false)}
          onSubmit={handleReportSubmit}
        />
      )}
    </div>
  );
};
